import type { Option } from 'effect';
import type * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import type * as Atom from 'effect/unstable/reactivity/Atom';

import type { Cart } from '../../capability/cart/cartSchema';
import type { AddToCartDeps } from './addToCart';
import { makeCartMutationPermit } from './cartMutationQueue';
import { makeCartMutations } from './cartMutations';
import { makeCartState, quantityOfVariantInCart } from './cartState';
import { CART_WRITE_SETTLE_DELAY, CART_WRITE_TIMEOUT } from './cartTransport';

type CartSessionAdapter<P, E, R, RT> = Pick<
  AddToCartDeps<P, Cart, E, R, RT>,
  'describe' | 'write' | 'track'
>;

/** One browser Cart session owns its state and mutation permit. */
export const makeCartSession = <ERead>(
  cartAtom: Atom.Atom<AsyncResult.AsyncResult<Option.Option<Cart>, ERead>>
) => {
  const state = makeCartState(cartAtom);
  const withPermit = makeCartMutationPermit();

  const mutations = <P, EWrite, R, RT>(adapter: CartSessionAdapter<P, EWrite, R, RT>) =>
    makeCartMutations({
      ...adapter,
      state,
      withPermit,
      cartAtom,
      quantityOf: quantityOfVariantInCart,
      writeTimeout: CART_WRITE_TIMEOUT,
      settleDelay: CART_WRITE_SETTLE_DELAY,
    });

  return { state, mutations };
};
